import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ContentHeader, ContentScroll } from "features/layout/components";
import {
  CreateOrEditTemplate,
  DesktopTemplate,
  MobileTemplate,
  TemplatePreview,
} from "features/templates/components";
import { TEMPLATE_TYPES } from "features/templates/constants";
import type {
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "features/templates/schemas";
import type { TemplateType } from "features/templates/types";

import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import DiscardNewTemplateDialog from "./DiscardNewTemplateDialog";

export default function NewTemplate() {
  const isDesktop = useTwBreakpoint("lg");
  const navigate = useNavigate();
  const { search } = useLocation();
  const { accounts } = useSelectedWorkspaceContext();
  const { templateType } = useParams();

  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<Exclude<TemplateSchemaType, VoiceTemplateSchemaType>>();

  const newTemplateType = templateType.toUpperCase() as TemplateType;

  useEffect(() => {
    setPreviewTemplate(undefined);
  }, [newTemplateType]);

  return (
    <>
      <CreateOrEditTemplate
        initialData={newTemplateType}
        onSuccess={({ id }) => navigate({ pathname: `../${id}`, search })}
      >
        {(onSubmit, isLoading, form) => {
          const updatedTemplate = form.watch();
          const previewComponent = updatedTemplate.type !==
            "VOICE_TEMPLATE" && (
            <TemplatePreview
              previewTemplate={previewTemplate}
              updatedTemplate={updatedTemplate}
              senderProfiles={accounts}
              onUpdatePreview={() => setPreviewTemplate(updatedTemplate)}
              hasError={"message" in form.formState.errors}
            />
          );
          const saveButton = (
            <Button onClick={onSubmit} isLoading={isLoading}>
              Save
            </Button>
          );
          return (
            <>
              {isDesktop ? (
                <>
                  <ContentHeader>
                    <h2 className="text-md-bold lg:text-lg-bold">
                      {`${TEMPLATE_TYPES[newTemplateType].shortLabel} template`}
                    </h2>
                  </ContentHeader>

                  <ContentScroll className="p-4 gap-y-4">
                    <DesktopTemplate
                      form={form}
                      previewComponent={previewComponent}
                      isCreatingNew
                    />
                  </ContentScroll>

                  {/* Footer */}
                  <div className="p-2.5 mt-auto border-t border-black-200 flex justify-between">
                    <Button
                      variant="secondary-black"
                      onClick={() => setIsDiscardOpen(true)}
                    >
                      Discard
                    </Button>
                    {saveButton}
                  </div>
                </>
              ) : (
                <MobileTemplate
                  form={form}
                  previewComponent={previewComponent}
                  updatePreview={() => {
                    if (updatedTemplate.type !== "VOICE_TEMPLATE") {
                      setPreviewTemplate(updatedTemplate);
                    }
                  }}
                  saveButton={saveButton}
                  onClose={() => setIsDiscardOpen(true)}
                />
              )}
            </>
          );
        }}
      </CreateOrEditTemplate>

      <DiscardNewTemplateDialog
        isOpen={isDiscardOpen}
        setIsOpen={setIsDiscardOpen}
      />
    </>
  );
}
